import { Timestamp } from "firebase/firestore";
import type { TimestampLike } from "@event-planner/shared";

/**
 * Convert a `datetime-local` input value (e.g., "2026-04-15T09:00") to a
 * Firestore Timestamp in UTC, interpreting the input as being in the given
 * IANA timezone.
 *
 * Uses Intl.DateTimeFormat for DST-safe conversion.
 */
export function dateTimeLocalToTimestamp(
  localDateTimeString: string,
  timezone: string,
): Timestamp {
  // datetime-local gives us "YYYY-MM-DDTHH:mm" in the event's timezone.
  // We need to figure out the UTC offset for that moment in that timezone,
  // then construct the correct UTC Date.

  // Parse the components from the input string
  const [datePart, timePart] = localDateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);

  // Create a Date assuming UTC, then use formatToParts to find the timezone offset
  // Strategy: create a rough UTC guess, use Intl to find the offset at that moment,
  // then adjust.
  const roughUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes));

  const offsetMinutes = getTimezoneOffsetMinutes(roughUtc, timezone);

  // The local time is offset ahead of UTC, so UTC = local - offset
  const utcMs = Date.UTC(year, month - 1, day, hours, minutes) - offsetMinutes * 60_000;

  // Verify: the offset might differ at the corrected UTC time (DST edge case).
  // Re-check with the corrected time.
  const corrected = new Date(utcMs);
  const correctedOffset = getTimezoneOffsetMinutes(corrected, timezone);

  const finalUtcMs =
    correctedOffset !== offsetMinutes
      ? Date.UTC(year, month - 1, day, hours, minutes) - correctedOffset * 60_000
      : utcMs;

  return Timestamp.fromDate(new Date(finalUtcMs));
}

/**
 * Convert a Firestore Timestamp to a `datetime-local` input value string
 * (e.g., "2026-04-15T09:00") in the given IANA timezone.
 */
export function timestampToDateTimeLocal(
  ts: TimestampLike,
  timezone: string,
): string {
  const date = ts.toDate();

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "00";

  // en-CA formats as YYYY-MM-DD which is what we need
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/**
 * Get the UTC offset in minutes for a given timezone at a given moment.
 * Positive = ahead of UTC (e.g., +60 for CET), negative = behind (e.g., -300 for EST).
 */
function getTimezoneOffsetMinutes(date: Date, timezone: string): number {
  // Format the date in the target timezone and in UTC, then compute the difference.
  const utcParts = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(date);

  const tzParts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(date);

  const toMinutes = (parts: Intl.DateTimeFormatPart[]) => {
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);
    const y = get("year");
    const m = get("month");
    const d = get("day");
    let h = get("hour");
    // Intl can return 24 for midnight in some locales
    if (h === 24) h = 0;
    const min = get("minute");
    // Convert to a comparable minute count (rough, but works for offset calc within a few days)
    return ((y * 12 + m) * 31 + d) * 24 * 60 + h * 60 + min;
  };

  return toMinutes(tzParts) - toMinutes(utcParts);
}
